import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Loader2, RefreshCw, Eye, Clock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/App";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  SUBMITTED: "bg-slate-100 text-slate-700",
  ACKNOWLEDGED: "bg-blue-100 text-blue-700",
  IN_PROGRESS: "bg-amber-100 text-amber-700",
  COMPLETED: "bg-emerald-100 text-emerald-700",
  REOPENED: "bg-red-100 text-red-700",
};

export default function RequestServiceServiceDesk() {
  const auth = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [departmentFilter, setDepartmentFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const fetchAll = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams();
      if (departmentFilter !== "all") params.append("department", departmentFilter);
      if (statusFilter !== "all") params.append("status", statusFilter);
      const res = await axios.get(`${API}/request/requests?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRequests(res.data);
    } catch (err) {
      toast.error("Failed to load requests");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchAll();
  }, [departmentFilter, statusFilter]);

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="font-['Barlow_Condensed'] text-3xl font-bold text-slate-800">
          Service Desk
        </h1>
        <p className="text-slate-500 mt-1">Unified queue for all service requests</p>
      </div>

      <div className="flex flex-wrap gap-3 mb-6">
        <div className="w-44">
          <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
            <SelectTrigger>
              <SelectValue placeholder="Department" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Departments</SelectItem>
              <SelectItem value="IT">IT</SelectItem>
              <SelectItem value="Logistics">Logistics</SelectItem>
              <SelectItem value="Security">Security</SelectItem>
              <SelectItem value="Catering">Catering</SelectItem>
              <SelectItem value="Medical">Medical</SelectItem>
              <SelectItem value="OfficeSupply">Office Supply</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="w-44">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger>
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="SUBMITTED">Submitted</SelectItem>
              <SelectItem value="ACKNOWLEDGED">Acknowledged</SelectItem>
              <SelectItem value="IN_PROGRESS">In Progress</SelectItem>
              <SelectItem value="COMPLETED">Completed</SelectItem>
              <SelectItem value="REOPENED">Reopened</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button variant="outline" onClick={fetchAll}>
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
        </div>
      ) : requests.length === 0 ? (
        <div className="text-center py-20 text-slate-500">
          <p className="text-lg">No requests in queue</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left">
                <th className="pb-3 font-semibold text-slate-600">ID</th>
                <th className="pb-3 font-semibold text-slate-600">Service</th>
                <th className="pb-3 font-semibold text-slate-600">Department</th>
                <th className="pb-3 font-semibold text-slate-600">Status</th>
                <th className="pb-3 font-semibold text-slate-600">Priority</th>
                <th className="pb-3 font-semibold text-slate-600">Requester</th>
                <th className="pb-3 font-semibold text-slate-600">Assigned</th>
                <th className="pb-3 font-semibold text-slate-600">Created</th>
                <th className="pb-3 font-semibold text-slate-600"></th>
              </tr>
            </thead>
            <tbody>
              {requests.map((req) => (
                <tr key={req.id} className="border-b border-slate-100 hover:bg-app">
                  <td className="py-3 font-mono text-xs text-slate-500">{req.id?.slice(-8)}</td>
                  <td className="py-3 font-medium text-slate-800">{req.service_type}</td>
                  <td className="py-3">
                    <span className="bg-slate-100 text-slate-600 px-2 py-0.5 rounded text-xs">
                      {req.department}
                    </span>
                  </td>
                  <td className="py-3">
                    <span
                      className={`px-2 py-0.5 rounded text-xs font-medium ${
                        STATUS_STYLES[req.status] || "bg-slate-100 text-slate-700"
                      }`}
                    >
                      {req.status?.replace("_", " ")}
                    </span>
                  </td>
                  <td className="py-3 text-xs">{req.priority}</td>
                  <td className="py-3 text-xs text-slate-600">{req.requested_by_name || req.requested_by}</td>
                  <td className="py-3 text-xs text-slate-600">{req.assigned_to_name || req.assigned_to || "-"}</td>
                  <td className="py-3 text-xs text-slate-400">
                    {req.created_at ? new Date(req.created_at).toLocaleDateString() : "-"}
                  </td>
                  <td className="py-3">
                    <Link to={`/request-service/my-requests/${req.id}`}>
                      <Button variant="ghost" size="sm">
                        <Eye className="w-4 h-4" />
                      </Button>
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
